"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { Button } from "../ui/button";
import { useRouter } from "next/navigation";
import { logout } from "@/app/actions/auth";
import { ChevronDown, User, Menu, X, Search, ShoppingCart } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { usePathname } from "next/navigation";
import { cn } from "@/app/lib/utils";
import { useCategories, type MenuItem } from "@/app/hooks/use-categories";
import Image from "next/image";
import { AnnouncementBar } from "./announcement-bar";
import { getPublicSetting } from "@/app/lib/api/public";

interface NavbarProps {
  isLoggedIn: boolean;
  username?: string;
}

export function Navbar({ isLoggedIn, username }: NavbarProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [hoveredCategory, setHoveredCategory] = useState<{
    label: string;
    href: string;
    image_url?: string | null;
  } | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [logo, setLogo] = useState<string | null>(null);
  const { menuItems } = useCategories();
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchLogo = async () => {
      const logo = await getPublicSetting(1);
      setLogo(logo.image_url || null);
    };
    fetchLogo();
  }, []);

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      router.refresh();
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/ara?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery("");
    }
  };

  return (
    <div className="sticky top-0 z-50">
      <AnnouncementBar />

      {/* Top Bar */}
      <div className="bg-white border-b border-white/20">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            <span className="h-[50px] w-[250px] relative">
              <Link href="/">
                {logo ? <Image src={logo} alt="Logo" fill={true} objectFit="contain"/> : null}
              </Link>
            </span>

            {/* Search Bar - Desktop */}
            <div className="hidden md:flex flex-1 max-w-md mx-8">
              <form onSubmit={handleSearch} className="relative w-full max-w-xs">
                <input
                  type="text"
                  placeholder="Ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="rounded-full bg-gray-100 w-full py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
              </form>
            </div>

            {/* User Actions */}
            <div className="flex items-center gap-4">
              {isLoggedIn ? (
                <div className="relative" ref={menuRef}>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 hover:text-orange-600"
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  >
                    <User className="h-5 w-5" />
                    <span className="hidden sm:inline">{username}</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>

                  <AnimatePresence>
                    {isUserMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute right-0 top-full mt-1 bg-white shadow-lg py-2 min-w-[240px] z-50 border border-white/20 rounded-xl"
                      >
                        <Link
                          href="/ayarlar"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-orange-600 hover:bg-orange-50/50"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Ayarlar
                        </Link>
                        <Link
                          href="/ayarlar/siparislerim"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-orange-600 hover:bg-orange-50/50"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Albüm Siparişlerim
                        </Link>
                        <Link
                          href="/ayarlar/canvas-siparislerim"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-orange-600 hover:bg-orange-50/50"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Kanvas Siparişlerim
                        </Link>
                        <Link
                          href="/ayarlar/baski-siparislerim"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-orange-600 hover:bg-orange-50/50"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Baskı Siparişlerim
                        </Link>
                        {/* <Link
                          href="/ayarlar/hediye-siparislerim"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-orange-600 hover:bg-orange-50/50"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Hediyelik Siparişlerim
                        </Link> */}
                        <div className="border-t border-white/20 my-1"></div>
                        <button
                          onClick={() => {
                            handleLogout();
                            setIsUserMenuOpen(false);
                          }}
                          className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50/50"
                        >
                          Çıkış Yap
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="hidden sm:flex items-center gap-2">
                  <Link href="/giris-yap">
                    <Button variant="ghost" className="hover:text-orange-600">
                      Giriş Yap
                    </Button>
                  </Link>
                  <Link href="/kayit-ol">
                    <Button className="bg-gradient-to-r from-orange-500 to-orange-500 hover:from-orange-600 hover:to-orange-700 text-white border-0">
                      Kayıt Ol
                    </Button>
                  </Link>
                </div>
              )}

              <div className="xl:hidden" ref={mobileMenuRef}>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="hover:text-orange-600"
                >
                  {isMobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar - Navigation */}
      <div className="bg-white border-b border-white/20 shadow-sm">
        <div className="container mx-auto">
          <div className="hidden xl:block">
            <ul className="flex items-center justify-start space-x-1">
              {menuItems.map((item) => (
                <li
                  key={item.label}
                  className="relative group"
                  onMouseEnter={() => {
                    setOpenMenu(item.label);
                    if (item.subItems?.length) {
                      setHoveredCategory(item.subItems[0]);
                    }
                  }}
                  onMouseLeave={() => {
                    setOpenMenu(null);
                    setHoveredCategory(null);
                  }}
                >
                  <Link
                    href={item.href}
                    className={cn(
                      "flex items-center px-4 py-3 text-sm transition-colors whitespace-nowrap",
                      pathname === item.href
                        ? "text-orange-700 font-medium"
                        : "text-gray-700 hover:text-orange-700"
                    )}
                  >
                    <span className="flex items-center gap-1">
                      {item.label}
                      {item.subItems && <ChevronDown className="h-4 w-4" />}
                    </span>
                  </Link>

                  {item.subItems && (
                    <AnimatePresence>
                      {openMenu === item.label && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute left-0 top-full bg-white shadow-lg py-4 min-w-[500px] z-50 border border-white/20 mt-1 rounded-xl"
                        >
                          <div className="flex items-stretch">
                            {/* Categories List */}
                            <div className="w-[200px]">
                              {item.subItems.map((subItem) => (
                                <Link
                                  key={subItem.label}
                                  href={subItem.href}
                                  className={cn(
                                    "flex items-center px-4 py-2 text-sm transition-colors",
                                    pathname === subItem.href
                                      ? "text-orange-700 font-medium bg-gray-50"
                                      : "text-gray-700 hover:text-orange-700",
                                    hoveredCategory?.label === subItem.label &&
                                      "bg-gray-50/50"
                                  )}
                                  onMouseEnter={() =>
                                    setHoveredCategory(subItem)
                                  }
                                >
                                  {subItem.label}
                                </Link>
                              ))}
                            </div>
                            {/* Category Image */}
                            <div className="w-[calc(100%-200px)] px-4">
                              {hoveredCategory?.image_url && (
                                <div className="relative aspect-[16/9] overflow-hidden min-h-[200px]">
                                  <Image
                                    src={hoveredCategory.image_url}
                                    alt={hoveredCategory.label}
                                    fill
                                    className="object-cover"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden fixed left-0 right-0 top-[105px] bg-white shadow-lg z-50 overflow-hidden border-b max-h-[calc(100vh-65px)] overflow-y-auto"
          >
            <div className="px-4 py-2">
              {/* Mobile Search */}
              <div className="mb-4 pt-2">
                <form onSubmit={handleSearch} className="relative w-full max-w-xs">
                  <input
                    type="text"
                    placeholder="Ara..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="rounded-full bg-gray-100 w-full py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                </form>
              </div>

              {!isLoggedIn && (
                <div className="sm:hidden flex flex-col gap-2 mb-4">
                  <Link href="/giris-yap" className="w-full">
                    <Button variant="ghost" className="w-full justify-center">
                      Giriş Yap
                    </Button>
                  </Link>
                  <Link href="/kayit-ol" className="w-full">
                    <Button className="w-full justify-center">Kayıt Ol</Button>
                  </Link>
                </div>
              )}

              {menuItems.map((item) => (
                <div key={item.label} className="py-1">
                  <Link
                    href={item.href}
                    className={cn(
                      "block py-2 text-sm px-3",
                      pathname === item.href
                        ? "text-orange-600 font-medium"
                        : "text-gray-700 hover:text-orange-600"
                    )}
                    onClick={() => !item.subItems && setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                  {item.subItems && (
                    <div className="ml-4 border-l border-gray-100">
                      {item.subItems.map((subItem) => (
                        <Link
                          key={subItem.label}
                          href={subItem.href}
                          className={cn(
                            "block py-2 text-sm px-3",
                            pathname === subItem.href
                              ? "text-orange-600 font-medium"
                              : "text-gray-700 hover:text-orange-600"
                          )}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
